import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import LoaderGif from '@components/common/LoaderGif/LoaderGif';
import Button from '@components/uikit/Button/Button';

import { DEFAULT_CURRENCY } from '@constants/currency';
import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useActionItemActiveReportQuery } from '@services/actionItem/useActionItemActiveReportQuery';
import { useActionItemQuery } from '@services/actionItem/useActionItemQuery';
import { useActionItemStaticDataQuery } from '@services/actionItem/useActionItemStaticDataQuery';
import { useSaveNewReportMutation } from '@services/report/useSaveNewReportMutation';
import { useSaveNewWwReportMutation } from '@services/report/useSaveNewWwReportMutation';
import { useSaveWithoutReportMutation } from '@services/report/useSaveWithoutReportMutation';
import { useSaveWithoutWwReportMutation } from '@services/report/useSaveWithoutWwReportMutation';

import { selectAppRtl } from '@store/useAppStore';
import { useAppPersistActions } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import ModalSteps from '../ModalSteps/ModalSteps';
import { COMPLETION_REPORT_FIELDS, completionReportDefaultValues } from './constants/schemaCompletionReport';
import { getCompletionReportSteps } from './helpers/getCompletionReportSteps';
import { getCostAvoided } from './helpers/getCostAvoidedAi';
import { getCostAvoidedValue } from './helpers/getCostAvoidedValue';
import style from './style.module.scss';

const CompletionReportModal = ({ actionItemId, controls }) => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();

  const { setModalClose, setSecondaryModal, setSecondaryModalClose } = useModalActions();

  const { data: actionItem, isFetching: isAiFetching } = useActionItemQuery({ actionItemId });

  const { data: aiStaticData, isFetching: isAiStaticFetching } = useActionItemStaticDataQuery({
    actionItemId,
  });
  const { data: reportData, isFetching: isReportsFetching } = useActionItemActiveReportQuery({
    actionItemId,
  });

  const costSavingSettings = aiStaticData?.costSavingSettings || {};
  const costSavingTooltips = aiStaticData?.costSavingTooltips;
  const costSavingTemplateCurrency = aiStaticData?.costSavingTemplateCurrency || null;
  const hasTemplateCurrency = !!costSavingTemplateCurrency?.symbol;

  const [step, setStep] = useState(0);
  const [noActionsSwitcher, setNoActionsSwitcher] = useState(1);

  const { setAppPersistDowntimeType } = useAppPersistActions();

  const costAvoidedAI = getCostAvoided(reportData, costSavingSettings);
  const hasLphAvoided = !!costSavingSettings?.use_lph_avoided;
  const stepsList = getCompletionReportSteps(controls?.isWwAi, noActionsSwitcher, hasLphAvoided);

  const currentStep = stepsList[step];
  const CurrentStepComponent = currentStep?.stepComponent;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState: { dirtyFields },
  } = useForm({
    mode: 'all',
    resolver: currentStep?.schema,
    values: completionReportDefaultValues(actionItem, reportData, hasTemplateCurrency, controls),
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const [downtimeAvoided, downtimeType, equipmentLaborSaving, lphAvoided, anyRepairsOrMaintenance] = useWatch(
    {
      control,
      name: [
        COMPLETION_REPORT_FIELDS.DOWNTIME_AVOIDED,
        COMPLETION_REPORT_FIELDS.DOWNTIME_TYPE,
        COMPLETION_REPORT_FIELDS.EQUIPMENT_LABOR_SAVINGS,
        COMPLETION_REPORT_FIELDS.LPH_AVOIDED,
        COMPLETION_REPORT_FIELDS.ANY_REPAIRS_OR_MAINTENANCE,
      ],
    },
  );

  const hasMetricsWarning =
    +downtimeAvoided === 0 || (!!costSavingSettings?.use_lph_avoided && +lphAvoided === 0);

  useEffect(() => {
    if (anyRepairsOrMaintenance === 'yes') {
      setNoActionsSwitcher(0);
    } else {
      setNoActionsSwitcher(1);
      const isNoActions = anyRepairsOrMaintenance === 'no_actions';
      setValue(COMPLETION_REPORT_FIELDS.IS_NO_ACTIONS, +isNoActions);
    }
  }, [anyRepairsOrMaintenance]);

  const { mutate: saveNewReportMutate, isLoading: saveNewReportLoading } = useSaveNewReportMutation({
    actionItemId,
  });
  const { mutate: saveNewWwReportMutate, isLoading: saveNewWwReportLoading } = useSaveNewWwReportMutation({
    actionItemId,
  });
  const { mutate: saveWithoutReportMutate, isLoading: saveWithoutReportLoading } =
    useSaveWithoutReportMutation({ actionItemId });
  const { mutate: saveWithoutWwReportMutate, isLoading: saveWithoutWwReportLoading } =
    useSaveWithoutWwReportMutation({ actionItemId });

  const isLoadingData =
    saveNewReportLoading || saveNewWwReportLoading || saveWithoutReportLoading || saveWithoutWwReportLoading;

  const onSuccess = () => {
    showToast({ variant: 'success', message: t('Your changes have been saved.') });
    handleClose(true);
  };

  const costAvoided = getCostAvoidedValue(
    costAvoidedAI,
    costSavingSettings,
    equipmentLaborSaving,
    lphAvoided,
    downtimeAvoided,
    downtimeType,
  );

  const submit = handleSubmit((data) => {
    if (controls?.isWwAi) {
      if (+data.is_no_actions === 0) {
        saveNewWwReportMutate(
          {
            is_resubmit_report: +controls?.isReportSubmitted,
            action_item_id: actionItem.id,
            text: data.text,
            file_name: data.file_name,
            /*---temp---*/ /* TODO check this */ ww_action_list_reason_group_id: 16,
            ww_action_list_other_reason_group_text: 'temp',
            severity: reportData?.severity || 1,
            /*----------*/
          },
          { onSuccess },
        );
      } else {
        saveWithoutWwReportMutate(
          {
            action_item_id: actionItem.id,
            description: data.text,
          },
          { onSuccess },
        );
      }
    } else {
      if (+noActionsSwitcher === 0) {
        let downtimeAvoidedValue = data.downtime_avoided || 0;
        let lphAvoidedValue = data.lph_avoided || 0;

        const causeCategorySeveritiesArr = data.causeCategorySeverities.map((category) => {
          return { severity: category.severity.id, cause_category: category.cause_category.value };
        });

        saveNewReportMutate(
          {
            is_resubmit_report: +controls?.isReportSubmitted,
            action_item_id: actionItem.id,
            text: data.text,
            cause_category_severities: causeCategorySeveritiesArr,
            cause: data.cause,
            wo_status: actionItem.wo_status || 0,
            replaced_parts: data.replaced_parts.map((part) => Object.values(part)),
            downtime_type: data.downtime_type,
            downtime_avoided: downtimeAvoidedValue,
            equipment_labor_saving: data.equipment_labor_saving || 0,
            lph_avoided: lphAvoidedValue,
            lph_additional_explanation: data.lph_additional_explanation,
            multiplication_factor:
              costSavingSettings.multiplication_factor_in_currency ??
              costSavingSettings.multiplication_factor,
            cost_avoided: costAvoided || 0,
            is_sensor_attached: +data.is_sensor_attached,
            file_name: data.file_name,
            currency_code: costSavingTemplateCurrency?.code,
          },
          {
            onSuccess: () => {
              showToast({ variant: 'success', message: t('Your changes have been saved.') });
              setAppPersistDowntimeType(data?.downtime_type);
              handleClose(true);
            },
          },
        );
      } else if (+data.is_no_actions === 0) {
        saveWithoutReportMutate(
          {
            no_actions_type: 2,
            action_item_id: actionItem.id,
            wo_status: actionItem.wo_status || 0,
            description: data.text,
            is_sensor_attached: +data.is_sensor_attached,
            no_actions_group_id: data.no_actions_group_id,
            no_actions_group_other_text: data.no_actions_group_other_text,
          },
          { onSuccess },
        );
      } else {
        saveWithoutReportMutate(
          {
            no_actions_type: 1,
            action_item_id: actionItem.id,
            wo_status: actionItem.wo_status || 0,
            description: data.text,
            is_sensor_attached: +data.is_sensor_attached,
          },
          { onSuccess },
        );
      }
    }
  });

  const progressBarSteps = stepsList.slice(0, stepsList.length - 1);

  const handleClose = (isSuccess) => {
    if (!isSuccess && Object.keys(dirtyFields).length) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Are you sure you want to exit without saving the changes?'),
          onConfirm: () => {
            setModalClose();
            reset();
          },
        },
      });
    } else {
      setModalClose();
      reset();
    }
  };

  const handleBack = () => {
    const previousStep = currentStep.previousStepIndex;
    if (previousStep === 'close') {
      handleClose();

      return;
    }

    setStep(previousStep);
  };

  const handleNextStep = async () => {
    const nextStep = currentStep.nextStepIndex;
    if (nextStep === 'end') {
      submit();

      return;
    }

    const isValid = await trigger();

    if (isValid) {
      if (nextStep === 3 && hasMetricsWarning) {
        setSecondaryModal({
          name: MODAL_NAMES.modalConfirm,
          options: {
            bodyTitle: t('Confirm'),
            bodyContent: t(
              'You indicated that maintenance was performed; however, you did not specify any hours in the Unplanned Downtime Avoided (Hours) field. Is this accurate?',
            ),
            onConfirm: () => {
              setSecondaryModalClose();
              setStep(nextStep);
            },
          },
        });

        return;
      }

      setStep(nextStep);
    }
  };

  return (
    <div
      className={cx(style.completionReportModal, {
        [style.bodyRTL]: rtl,
      })}
    >
      <ModalHeader
        title={t(
          controls?.showEditReportBtn || controls?.showResubmitReportBtn || controls?.showWwResubmitReportBtn
            ? 'Edit Report'
            : 'Completion Report',
        )}
        onClose={handleClose}
      />
      <ModalSteps
        stepName={`${t('step')} ${currentStep?.step || ''}: ${t(currentStep?.stepName || '')}`}
        currentStep={currentStep?.step}
        steps={progressBarSteps}
        variant="withoutBorder"
      />
      <div className={style.modalBody}>
        {isAiFetching || isAiStaticFetching || isReportsFetching ? (
          <div className={style.gifCenter}>
            <LoaderGif />
          </div>
        ) : (
          <CurrentStepComponent
            control={control}
            isWwAi={controls?.isWwAi}
            costAvoided={costAvoided}
            noActionsSwitcher={+noActionsSwitcher}
            costSavingSettings={costSavingSettings}
            costSavingTooltips={costSavingTooltips}
            currency={costSavingTemplateCurrency || actionItem?.facility.currency || DEFAULT_CURRENCY}
          />
        )}
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Back')} onClick={handleBack} />
        <Button
          type="primary"
          variant={'medium'}
          text={t(currentStep?.nextStepIndex === 'end' ? 'Submit' : 'Next')}
          disabled={isLoadingData}
          onClick={handleNextStep}
        />
      </div>
    </div>
  );
};

export default CompletionReportModal;
